import React, { useState } from 'react';
import { useStore } from '../store/StoreContext';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { Button } from '../components/UI/Button';
import { Modal } from '../components/UI/Modal';
import { Input, Select } from '../components/UI/Input';
import { generateId } from '../utils/helpers';
import supabase from '../supabase/supabase';

export const UsersPage = () => {
  const { state, refresh } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ email: '', role: 'user' });
  const [inviting, setInviting] = useState(false);

  const members = Object.values(state.workspaceMembers || {});
  const users = state.users || {};

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      // 1. Check if user exists
      let { data: user } = await supabase.from('users_2024').select('*').eq('email', formData.email.toLowerCase()).maybeSingle();
      
      // 2. If not, create a skeleton user
      if (!user) {
        const userId = generateId('u');
        const { data: newUser } = await supabase.from('users_2024').insert({
          id: userId,
          email: formData.email.toLowerCase(),
          name: formData.email.split('@')[0],
          password: 'password' // Default password for new invites
        }).select().single();
        user = newUser;
      }

      // 3. Add to workspace_members
      await supabase.from('workspace_members_2024').insert({
        id: generateId('wm'),
        workspace_id: state.currentWorkspace.id,
        user_id: user.id,
        role: formData.role
      });

      await refresh();
      setIsModalOpen(false);
      setFormData({ email: '', role: 'user' });
    } catch (err) {
      alert(err.message);
    } finally {
      setInviting(false);
    }
  };

  const removeMember = async (membershipId) => {
    if (confirm('Remove this member from the workspace?')) {
      await supabase.from('workspace_members_2024').delete().eq('id', membershipId);
      await refresh();
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8 h-full overflow-y-auto bg-base-900">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">Workspace Members</h1>
          <p className="text-sm text-gray-400">Manage who has access to the {state.currentWorkspace?.name}.</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <SafeIcon name="UserPlus" className="mr-2" /> Invite Member
        </Button>
      </div>

      <div className="bg-base-800 rounded-2xl border border-base-700 overflow-hidden shadow-xl">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-base-900/50 border-b border-base-700">
              <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Member</th>
              <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Role</th>
              <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-700">
            {members.map(m => {
              const u = users[m.user_id];
              if (!u) return null;
              return (
                <tr key={m.id} className="hover:bg-base-700/30 transition-colors group">
                  <td className="p-4">
                    <div className="flex items-center gap-4">
                      <img src={u.avatar || `https://ui-avatars.com/api/?name=${u.name}&background=6C63FF&color=fff`} className="w-10 h-10 rounded-full border border-base-600 object-cover" alt="" />
                      <div>
                        <div className="font-semibold text-white">{u.name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-tighter border ${m.role === 'admin' ? 'bg-primary/10 text-primary border-primary/20' : 'bg-accent/10 text-accent border-accent/20'}`}>
                      {m.role}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    {u.id !== state.currentUser?.id && (
                      <button onClick={() => removeMember(m.id)} className="p-2 text-gray-400 hover:text-red-500 transition-opacity opacity-0 group-hover:opacity-100">
                        <SafeIcon name="Trash2" className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Invite New Member">
        <form onSubmit={handleInvite} className="space-y-6">
          <Input label="Email Address" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required placeholder="colleague@company.com" />
          <Select label="Workspace Role" value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })} options={[{ label: 'Member (View/Edit)', value: 'user' }, { label: 'Admin (Full Access)', value: 'admin' }]} />
          <div className="flex justify-end gap-3 pt-6 border-t border-base-700">
            <Button variant="ghost" type="button" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={inviting}>{inviting ? 'Inviting...' : 'Send Invite'}</Button>
          </div>
        </form>
      </Modal>
    </motion.div>
  );
};